import React from 'react';
import styled from 'styled-components';

// Styled Components
const FormSection = styled.section`
  margin-bottom: 2rem;
  
  h3 {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #f8f9fa;
    color: var(--primary);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
  
  label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--dark);
  }
  
  .form-text {
    font-size: 0.85rem;
    color: var(--gray);
    margin-top: 0.25rem;
  }
  
  input, select, textarea {
    border: 2px solid ${props => props.hasError ? 'var(--primary)' : '#f0f0f0'};
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:focus {
      border-color: ${props => props.hasError ? 'var(--primary)' : 'var(--primary-light)'};
      box-shadow: 0 0 0 0.25rem ${props => props.hasError ? 'rgba(233, 64, 87, 0.25)' : 'rgba(233, 64, 87, 0.25)'};
    }
  }
  
  .error-message {
    color: var(--primary);
    font-size: 0.85rem;
    margin-top: 0.25rem;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.1)' : '#f8f9fa'};
  border: 1px solid ${props => props.checked ? 'var(--primary)' : '#e9ecef'};
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.checked ? 'rgba(233, 64, 87, 0.15)' : '#e9ecef'};
  }
  
  input {
    margin-right: 0.5rem;
  }
`;

const EnvironmentSection = ({ formData, handleChange, errors }) => {
  return (
    <FormSection>
      <FormGroup hasError={!!errors.sleepLocation}>
        <label htmlFor="sleepLocation">Sleep Location</label>
        <select
          className="form-select"
          id="sleepLocation"
          name="sleepLocation"
          value={formData.sleepLocation}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Own cot/crib">Own cot/crib</option>
          <option value="Bassinet">Bassinet</option>
          <option value="Co-sleeping/bedsharing">Co-sleeping/bedsharing</option>
          <option value="Other">Other</option>
        </select>
        {errors.sleepLocation && <div className="error-message">{errors.sleepLocation}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.isDarkRoom}>
        <label htmlFor="isDarkRoom">Is the baby's sleep space dark?</label>
        <select
          className="form-select"
          id="isDarkRoom"
          name="isDarkRoom"
          value={formData.isDarkRoom}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        {errors.isDarkRoom && <div className="error-message">{errors.isDarkRoom}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.usesWhiteNoise}>
        <label htmlFor="usesWhiteNoise">Is white noise used?</label>
        <select
          className="form-select"
          id="usesWhiteNoise"
          name="usesWhiteNoise"
          value={formData.usesWhiteNoise}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        {errors.usesWhiteNoise && <div className="error-message">{errors.usesWhiteNoise}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.sleepwear}>
        <label htmlFor="sleepwear">Sleepwear used</label>
        <select
          className="form-select"
          id="sleepwear"
          name="sleepwear"
          value={formData.sleepwear}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Swaddle">Swaddle</option>
          <option value="Sleeping bag">Sleeping bag</option>
          <option value="None">None</option>
          <option value="Other">Other</option>
        </select>
        {errors.sleepwear && <div className="error-message">{errors.sleepwear}</div>}
      </FormGroup>
      
      <FormGroup>
        <label htmlFor="roomTemperature">Room temperature (°C)</label>
        <div className="temperature-slider-container" style={{ maxWidth: '400px', margin: '0 auto' }}>
          <div className="d-flex align-items-center">
            <span className="me-2 text-primary fw-bold">10°C</span>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="range"
                className="form-range"
                id="roomTemperature"
                name="roomTemperature"
                min="10"
                max="30"
                step="1"
                value={formData.roomTemperature || "20"}
                onChange={handleChange}
                style={{ 
                  height: '10px',
                  accentColor: 'var(--primary)',
                  cursor: 'pointer'
                }}
              />
              <div className="temperature-ticks" style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginTop: '5px',
                fontSize: '10px',
                color: 'var(--gray)'
              }}>
                <span>|</span>
                <span>|</span>
                <span>|</span>
                <span>|</span>
                <span>|</span>
              </div>
            </div>
            <span className="ms-2 text-primary fw-bold">30°C</span>
          </div>
          <div className="text-center mt-2">
            <span className="badge bg-primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
              {formData.roomTemperature || "20"}°C
            </span>
          </div>
        </div>
      </FormGroup>
      
      <FormGroup hasError={!!errors.roomSharing}>
        <label htmlFor="roomSharing">Does your baby sleep in the same room as a parent or caregiver?</label>
        <select
          className="form-select"
          id="roomSharing"
          name="roomSharing"
          value={formData.roomSharing}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
        {errors.roomSharing && <div className="error-message">{errors.roomSharing}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.parentingStylePreference}>
        <label htmlFor="parentingStylePreference">Parenting Style Preference</label>
        <select
          className="form-select"
          id="parentingStylePreference"
          name="parentingStylePreference"
          value={formData.parentingStylePreference}
          onChange={handleChange}
          required
        >
          <option value="">Select option</option>
          <option value="Gentle no-cry methods">Gentle no-cry methods</option>
          <option value="Gradual methods">Gradual methods</option>
          <option value="Okay with some crying">Okay with some crying</option>
        </select>
        {errors.parentingStylePreference && <div className="error-message">{errors.parentingStylePreference}</div>}
      </FormGroup>
      
      <FormGroup hasError={!!errors.mainGoals}>
        <label>Main Goal (select one or two)</label>
        <CheckboxGroup>
          <CheckboxLabel checked={formData.mainGoals.longerStretches}>
            <input
              type="checkbox"
              name="mainGoals.longerStretches"
              checked={formData.mainGoals.longerStretches}
              onChange={handleChange}
            />
            Longer stretches
          </CheckboxLabel>
          <CheckboxLabel checked={formData.mainGoals.selfSettling}>
            <input
              type="checkbox"
              name="mainGoals.selfSettling"
              checked={formData.mainGoals.selfSettling}
              onChange={handleChange}
            />
            Self-settling
          </CheckboxLabel>
          <CheckboxLabel checked={formData.mainGoals.betterNaps}>
            <input
              type="checkbox"
              name="mainGoals.betterNaps"
              checked={formData.mainGoals.betterNaps}
              onChange={handleChange}
            />
            Better naps
          </CheckboxLabel>
          <CheckboxLabel checked={formData.mainGoals.reducingNightWakings}>
            <input
              type="checkbox"
              name="mainGoals.reducingNightWakings"
              checked={formData.mainGoals.reducingNightWakings}
              onChange={handleChange}
            />
            Reducing night wakings
          </CheckboxLabel>
        </CheckboxGroup>
        {errors.mainGoals && <div className="error-message">{errors.mainGoals}</div>}
      </FormGroup>
      
      <FormGroup>
        <label htmlFor="extraNotes">Extra Notes (optional)</label>
        <textarea
          className="form-control"
          id="extraNotes"
          name="extraNotes"
          rows="3"
          value={formData.extraNotes}
          onChange={handleChange}
          placeholder="Is there anything else we should know about your baby? (e.g., allergies, reflux, special considerations)"
        ></textarea>
      </FormGroup>
    </FormSection>
  );
};

export default EnvironmentSection;
